import {
  Button,
  FormLabel,
  Grid,
  Paper,
  TextField,
  Typography,
  Autocomplete,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import TicketSearch from "../../../../Components/TicketSearch";
import React, { useState, useEffect } from "react";
import useAdd from "../../../../api/useAdd";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";
import ListEchoResult from "./ListEchoResult";
import { useTranslation } from "react-i18next";

const buttonStyle = {
  textTransform: "capitalize",
  backgroundColor: "#071952",
  fontWeight: "bold",
};

const EchoResultEntry = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [selectedTicket, setSelectedTicket] = useState(null);

  // Fetch echoes only for the selected ticket (ticket-first flow)
  const { data: echoTypes } = useFetchObjects(
    selectedTicket?.id != null ? ["patient-echo", "ticket", selectedTicket.id] : null,
    selectedTicket?.id != null
      ? `patient-echo/?ticket=${selectedTicket.id}`
      : "",
    token,
  );

  const { handleAdd, resetForm, loading } = useAdd("echo-results", token);

  const [formData, setFormData] = useState({
    patient_echo: "",
    image: null,
    findings: "",
    result_date: new Date().toISOString().split("T")[0],
  });

  const [errors, setErrors] = useState({});
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (resetForm) {
      setFormData({
        patient_echo: "",
        image: null,
        findings: "",
        result_date: new Date().toISOString().split("T")[0],
      });
    }
  }, [resetForm]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      patient_echo: "",
      patient_name: "",
      echo_type: "",
    }));
  }, [selectedTicket?.id]);

  const handleInputChange = (event) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
  };

  const handleEchoSelect = (event, selectedEcho) => {
    if (selectedEcho) {
      setFormData({
        ...formData,
        patient_echo: selectedEcho.id,
        patient_name: selectedEcho.patient_display,
        echo_type: selectedEcho.echo_type_display,
      });
    }
  };

  const handleFileChange = (event) => {
    setFormData({ ...formData, image: event.target.files[0] });
  };

  const validateFields = () => {
    const newErrors = {};

    if (!formData.patient_echo) {
      newErrors.patient_echo = t("echo.selectEcho");
    }

    if (!formData.findings.trim()) {
      newErrors.findings = t("echo.enterEchoFinding");
    }

    if (formData.image && !formData.image.type.startsWith("image/")) {
      newErrors.image = t("echo.uploadImage");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateFields()) {
      setOpen(true);
      return;
    }

    const formDataToSubmit = new FormData();

    for (let key in formData) {
      if (formData.hasOwnProperty(key)) {
        if (key === "image" && !formData[key]) {
          continue;
        }
        formDataToSubmit.append(key, formData[key]);
      }
    }

    handleAdd(formDataToSubmit);
  };

  const handleClose = () => setOpen(false);

  return (
    <Grid container maxWidth={"xl"} mt={2} justifyContent={"center"} mb={10}>
      <Grid item xs={12} lg={8}>
        <Paper sx={{ boxShadow: "0px 0px 0px ", padding: "30px 40px" }}>
          <Typography variant="h5" gutterBottom>
            {t("echo.echoResultEntry")}
          </Typography>

          <Grid container spacing={4} mt={2}>
            <Grid item xs={12}>
              <FormLabel>{t("echo.searchTicket") || "Search ticket"}</FormLabel>
              <TicketSearch
                value={selectedTicket}
                onChange={setSelectedTicket}
                placeholder={t("echo.searchByTicket") || "Search by ticket number"}
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormLabel>{t("echo.patientEcho")}</FormLabel>
              <Autocomplete
                options={echoTypes ?? []}
                getOptionLabel={(option) =>
                  `${option.patient_display} - ${option.echo_type_display}`
                }
                onChange={handleEchoSelect}
                disabled={!selectedTicket}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="patient_echo"
                    value={formData.patient_name || ""}
                    fullWidth
                    size="small"
                    placeholder={
                      selectedTicket
                        ? t("echo.selectPatientEcho")
                        : t("echo.selectTicketFirst") || "Select a ticket first"
                    }
                    error={!!errors.patient_echo}
                    helperText={errors.patient_echo}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormLabel>{t("echo.echoType")}</FormLabel>
              <TextField
                name="echo_type"
                value={formData.echo_type || ""}
                fullWidth
                size="small"
                disabled
              />
            </Grid>

            <Grid item xs={12} md={12}>
              <FormLabel>{t("echo.echoFindings")}</FormLabel>
              <TextField
                name="findings"
                value={formData.findings}
                onChange={handleInputChange}
                fullWidth
                size="small"
                multiline
                rows={4}
                placeholder={t("echo.enterEchoFindings")}
                error={!!errors.findings}
                helperText={errors.findings}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormLabel>{t("echo.uploadFile")}</FormLabel>
              <TextField
                type="file"
                inputProps={{ accept: "image/*" }}
                onChange={handleFileChange}
                fullWidth
                size="small"
                error={!!errors.image}
                helperText={errors.image}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormLabel>{t("echo.resultDate")}</FormLabel>
              <TextField
                type="date"
                name="result_date"
                onChange={handleInputChange}
                fullWidth
                size="small"
                defaultValue={new Date().toISOString().split("T")[0]}
              />
            </Grid>
          </Grid>

          <Grid container justifyContent={"flex-end"} mt={4}>
            <Button
              variant="contained"
              style={{ ...buttonStyle, marginLeft: "20px" }}
              onClick={handleSubmit}
              disabled={loading}
              startIcon={loading && <CircularProgress size={24} />}
            >
              {loading ? t("echo.loading") : t("echo.save")}
            </Button>
          </Grid>

          <Snackbar open={open} autoHideDuration={4000} onClose={handleClose}>
            <Alert
              onClose={handleClose}
              severity="error"
              sx={{ width: "100%" }}
            >
              {t("echo.correctError")}
            </Alert>
          </Snackbar>

          <Divider sx={{ marginY: 4 }} />

          <ListEchoResult />
        </Paper>
      </Grid>
    </Grid>
  );
};

export default EchoResultEntry;
